/**
 * Query seed configurations for candidate search
 * Software Engineers, Data Scientists, ML Engineers, Quantitative Finance, Frontend Engineers
 */

export interface HardCriteria {
  minYearsExperience?: number;
  requiredDegrees?: string[];
  requiredSkills?: string[];
  requiredLocations?: string[];
}

export interface SoftCriteria {
  preferredTitles?: string[];
  preferredCompanies?: string[];
  preferredSchools?: string[];
  preferredSkills?: string[];
  keywords?: string[];
  preferMoreExperience?: boolean;
}

export interface QuerySeed {
  query: string;
  hardCriteria?: HardCriteria;
  softCriteria?: SoftCriteria;
}

export interface QuerySeeds {
  [key: string]: QuerySeed;
}

export const QUERY_SEEDS: QuerySeeds = {
  software_engineer: {
    query: "Software engineer with experience in Python, Java, C++, algorithms, data structures, software development, problem solving",
    hardCriteria: {},
    softCriteria: {
      keywords: ["Python", "Java", "C++", "Software Engineer", "Machine Learning", "AWS", "Docker", "SQL"],
      preferredTitles: ["Software Engineer", "Senior Software Engineer", "Staff Software Engineer", "Software Developer"],
      preferredCompanies: ["Google", "Microsoft", "Meta", "Amazon", "Apple"],
      preferMoreExperience: false
    }
  },

  data_scientist: {
    query: "Data scientist with machine learning, Python, statistical modeling, deep learning, TensorFlow, PyTorch, data analysis",
    hardCriteria: {},
    softCriteria: {
      keywords: ["Python", "Machine Learning", "SQL", "Deep Learning", "TensorFlow", "PyTorch", "Statistical Modeling", "R"],
      preferredTitles: ["Data Scientist", "Senior Data Scientist", "Lead Data Scientist", "Machine Learning Engineer"],
      preferredCompanies: ["Google", "Meta", "Microsoft", "Amazon", "LinkedIn"],
      preferredSchools: ["Stanford University", "Carnegie Mellon University", "MIT"],
      preferMoreExperience: false
    }
  },

  research_scientist: {
    query: "Research scientist with PhD, publications, machine learning research, deep learning, computer vision, NLP, academic background",
    hardCriteria: {},
    softCriteria: {
      keywords: ["Machine Learning", "Deep Learning", "Python", "Research", "NLP", "Computer Vision", "TensorFlow", "PyTorch"],
      preferredTitles: ["Research Scientist", "Research Assistant", "Postdoctoral Researcher", "Graduate Research Assistant"],
      preferredCompanies: ["Google", "Meta", "Microsoft", "Stanford University", "Carnegie Mellon University"],
      preferredSchools: ["Stanford University", "MIT", "Carnegie Mellon University", "Berkeley"],
      preferMoreExperience: false
    }
  },

  quantitative_analyst: {
    query: "Quantitative analyst with financial modeling, risk management, Python, C++, statistical analysis, derivatives, trading",
    hardCriteria: {},
    softCriteria: {
      keywords: ["Python", "C++", "Financial Modeling", "Risk Management", "Statistical Modeling", "Machine Learning"],
      preferredTitles: ["Quantitative Analyst", "Quantitative Researcher", "Senior Quantitative Analyst", "CFO", "Chief Financial Officer"],
      preferredCompanies: ["Goldman Sachs", "Morgan Stanley", "Bloomberg LP", "Bank of America"],
      preferMoreExperience: false
    }
  },

  ml_engineer: {
    query: "Machine learning engineer with Python, TensorFlow, PyTorch, deep learning, model deployment, AWS, Docker, Kubernetes",
    hardCriteria: {},
    softCriteria: {
      keywords: ["Python", "Machine Learning", "Deep Learning", "TensorFlow", "PyTorch", "AWS", "Docker", "Kubernetes"],
      preferredTitles: ["Machine Learning Engineer", "Senior Machine Learning Engineer", "ML Engineer", "Data Scientist"],
      preferredCompanies: ["Google", "Meta", "Amazon", "Microsoft", "Uber"],
      preferMoreExperience: false
    }
  },

  tech_leadership: {
    query: "Technical leadership with software engineering background, team management, system design, scalable architecture, Python, Java",
    hardCriteria: {
      minYearsExperience: 6
    },
    softCriteria: {
      keywords: ["Leadership", "Python", "Java", "AWS", "System Design", "Machine Learning"],
      preferredTitles: ["Principal Software Engineer", "Staff Software Engineer", "Lead Data Scientist", "Senior Software Engineer"],
      preferredCompanies: ["Google", "Microsoft", "Meta", "Amazon", "Apple"],
      preferMoreExperience: true
    }
  }
};

export const QUERY_SEED_LABELS: { [key: string]: string } = {
  software_engineer: "Software Engineer",
  data_scientist: "Data Scientist",
  research_scientist: "Research Scientist",
  quantitative_analyst: "Quantitative Analyst",
  ml_engineer: "ML Engineer",
  tech_leadership: "Tech Leadership (6+ years)"
};
